import React, { useState, useEffect } from 'react';
import Loading from '../components/Loading';
import config from '../config';
import {
  TextField,
  Button,
  Box,
  Container,
  Typography,
  Collapse,
  Paper,
  Grid,
  Card,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Fade,
  GlobalStyles,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../UserContext';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import GroupsIcon from '@mui/icons-material/Groups';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ScoreboardIcon from '@mui/icons-material/Scoreboard';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';

const Auth = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [adminCode, setAdminCode] = useState('');
  const [volunteerCode, setVolunteerCode] = useState('');
  const [showAdminCode, setShowAdminCode] = useState(false);
  const [showVolunteerCode, setShowVolunteerCode] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { userData, updateUserData } = useUser();

  // Redirect if user is already logged in
  useEffect(() => {
    if (userData) {
      switch (userData.role) {
        case 'admin':
          navigate('/admin');
          break;
        case 'volunteer':
          navigate('/volunteer/scores');
          break;
        default:
          navigate('/dashboard');
          break;
      }
    }
  }, [userData, navigate]);

  const toggleAuthMode = () => {
    setIsLogin(!isLogin);
    setError('');
    setSuccessMessage('');
    setAdminCode('');
    setVolunteerCode('');
    setShowAdminCode(false);
    setShowVolunteerCode(false);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const path = isLogin ? '/login' : '/signup';

    // Email format validation (for both login and signup)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address.');
      setSuccessMessage('');
      return;
    }

    // Frontend password validation for signup
    if (!isLogin) {
      if (password.length < 8) {
        setError('Password must be at least 8 characters long.');
        setSuccessMessage('');
        return;
      }
    }

    setLoading(true); // Set loading to true before API call

    try {
      const response = await fetch(`${config.apiBaseUrl}${path}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
          ...(isLogin
            ? {}
            : {
                adminCode: adminCode || undefined,
                volunteerCode: volunteerCode || undefined,
              }),
        }),
      });

      const data = await response.json();

      if (response.ok) {
        if (isLogin) {
          localStorage.setItem('token', data.token);
          updateUserData(data.user);
        } else {
          setError('');
          setSuccessMessage('Account created successfully! You can now login.');
          setIsLogin(true);
          setEmail('');
          setPassword('');
          setAdminCode('');
          setVolunteerCode('');
          setShowAdminCode(false);
          setShowVolunteerCode(false);
        }
      } else {
        setSuccessMessage('');
        setError(''); // Clear error first
        setTimeout(() => setError(data.message || 'Authentication failed.'), 10);
      }
    } catch (error) {
      setSuccessMessage('');
      setError(''); // Clear error first
      setTimeout(() => setError('An error occurred. Please try again.'), 10);
    } finally {
      setLoading(false); // Set loading to false after API call
    }
  };

  const features = [
    {
      icon: <GroupsIcon />,
      title: 'Team Management',
      description: 'Register and manage your teams with ease',
    },
    {
      icon: <SportsSoccerIcon />,
      title: 'Match Scheduling',
      description: 'Schedule and organize matches efficiently',
    },
    {
      icon: <ScoreboardIcon />,
      title: 'Live Scores',
      description: 'Keep track of live match scores and statistics',
    },
    {
      icon: <EmojiEventsIcon />,
      title: 'Tournament Updates',
      description: 'Stay updated with tournament progress and standings',
    },
  ];

  const roleFeatures = {
    admin: [
      'Full access to tournament management',
      'Team and player approval',
      'Match scheduling control',
      'System configuration',
    ],
    volunteer: [
      'Match score updates',
      'Live game statistics',
      'Player performance tracking',
      'Match report submission',
    ],
  };

  return (
    <Container
      maxWidth="xl"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        px: { xs: 2, md: 4, lg: 8 },
        py: { xs: 4, md: 6 },
        background: 'linear-gradient(135deg, #0f0f0f 0%, #1a1a1a 100%)',
      }}
    >
      {/* Loading component */}
      {loading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            zIndex: 9999,
            backdropFilter: 'blur(5px)',
          }}
        >
          <Loading />
        </Box>
      )}

      <GlobalStyles
        styles={{
          'input:-webkit-autofill, input:-webkit-autofill:hover, input:-webkit-autofill:focus': {
            '-webkit-box-shadow': '0 0 0 30px #1a1a1a inset !important',
            '-webkit-text-fill-color': '#FFFFFF !important',
            transition: 'background-color 5000s ease-in-out 0s',
          },
          '@keyframes gradientBorder': {
            '0%': {
              backgroundPosition: '0% 50%',
            },
            '50%': {
              backgroundPosition: '100% 50%',
            },
            '100%': {
              backgroundPosition: '0% 50%',
            },
          },
        }}
      />
      <Grid
        container
        spacing={{ xs: 4, md: 6 }}
        alignItems="stretch"
        sx={{ minHeight: { md: '80vh' } }}
      >
        {/* Left side - Auth form */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={3}
            sx={{
              height: '100%',
              p: { xs: 4, md: 6 },
              borderRadius: 2,
              background: 'linear-gradient(145deg, #1a1a1a 0%, #2a2a2a 100%)',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                border: '2px solid transparent',
                borderRadius: 2,
                background: 'linear-gradient(300deg, #FFD700, #FFA500, #FF8C00)',
                backgroundSize: '300% 300%',
                animation: 'gradientBorder 15s ease infinite',
                WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                WebkitMaskComposite: 'xor',
                maskComposite: 'exclude',
              },
              '&:hover::before': {
                animationDuration: '10s',
              },
            }}
          >
            <Typography
              variant="h3"
              sx={{
                textAlign: 'center',
                color: '#FFD700',
                fontWeight: 'bold',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                mb: 4,
              }}
            >
              {isLogin ? 'Welcome Back!' : 'Join Our Community'}
            </Typography>

            {!isLogin && (
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  bgcolor: 'rgba(255, 215, 0, 0.1)',
                  border: '1px solid rgba(255, 215, 0, 0.3)',
                  borderRadius: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: '#FFD700',
                    textAlign: 'center',
                    fontWeight: 500,
                  }}
                >
                  If you are registering your team, you do not need to sign up as Admin or
                  Volunteer. Those roles are reserved for Mirmire's management team and can be
                  ignored.
                </Typography>
              </Box>
            )}

            {error && (
              <Fade in={!!error}>
                <Typography
                  color="error"
                  sx={{
                    mb: 2,
                    p: 1,
                    bgcolor: 'rgba(255, 0, 0, 0.1)',
                    borderRadius: 1,
                    textAlign: 'center',
                    fontSize: '0.8rem',
                  }}
                >
                  {error}
                </Typography>
              </Fade>
            )}
            {successMessage && (
              <Fade in={!!successMessage}>
                <Typography
                  sx={{
                    mb: 2,
                    p: 1,
                    color: '#4caf50',
                    bgcolor: 'rgba(76, 175, 80, 0.1)',
                    borderRadius: 1,
                    textAlign: 'center',
                  }}
                >
                  {successMessage}
                </Typography>
              </Fade>
            )}

            <form
              onSubmit={handleSubmit}
              style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}
            >
              <TextField
                fullWidth
                label="Email"
                variant="outlined"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#1a1a1a',
                    '& fieldset': {
                      borderColor: 'rgba(255, 215, 0, 0.3)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 215, 0, 0.5)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#FFD700',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#FFD700',
                  },
                  '& .MuiOutlinedInput-input': {
                    color: '#FFFFFF',
                    backgroundColor: '#1a1a1a',
                  },
                }}
              />
              <TextField
                fullWidth
                label="Password"
                type="password"
                variant="outlined"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#1a1a1a',
                    '& fieldset': {
                      borderColor: 'rgba(255, 215, 0, 0.3)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 215, 0, 0.5)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#FFD700',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#FFD700',
                  },
                  '& .MuiOutlinedInput-input': {
                    color: '#FFFFFF',
                    backgroundColor: '#1a1a1a',
                  },
                }}
              />

              {!isLogin && (
                <Box sx={{ mb: 2 }}>
                  <Grid container spacing={2} justifyContent="center">
                    <Grid item xs={6}>
                      <Button
                        fullWidth
                        startIcon={<AdminPanelSettingsIcon />}
                        onClick={() => {
                          setShowAdminCode(!showAdminCode);
                          if (!showAdminCode) setShowVolunteerCode(false);
                        }}
                        sx={{
                          color: showAdminCode ? '#FFD700' : '#FFFFFF',
                          borderColor: showAdminCode ? '#FFD700' : 'rgba(255, 255, 255, 0.3)',
                          '&:hover': {
                            borderColor: '#FFD700',
                            bgcolor: 'rgba(255, 215, 0, 0.1)',
                          },
                        }}
                        variant="outlined"
                      >
                        {showAdminCode ? 'Hide Admin' : 'Admin Signup'}
                      </Button>
                    </Grid>
                    <Grid item xs={6}>
                      <Button
                        fullWidth
                        startIcon={<VolunteerActivismIcon />}
                        onClick={() => {
                          setShowVolunteerCode(!showVolunteerCode);
                          if (!showVolunteerCode) setShowAdminCode(false);
                        }}
                        sx={{
                          color: showVolunteerCode ? '#FFD700' : '#FFFFFF',
                          borderColor: showVolunteerCode ? '#FFD700' : 'rgba(255, 255, 255, 0.3)',
                          '&:hover': {
                            borderColor: '#FFD700',
                            bgcolor: 'rgba(255, 215, 0, 0.1)',
                          },
                        }}
                        variant="outlined"
                      >
                        {showVolunteerCode ? 'Hide Volunteer' : 'Volunteer Signup'}
                      </Button>
                    </Grid>
                  </Grid>

                  <Collapse in={showAdminCode}>
                    <TextField
                      fullWidth
                      label="Admin Code"
                      type="password"
                      variant="outlined"
                      value={adminCode}
                      onChange={e => setAdminCode(e.target.value)}
                      sx={{
                        mt: 2,
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#1a1a1a',
                          '& fieldset': {
                            borderColor: 'rgba(255, 215, 0, 0.3)',
                          },
                          '&:hover fieldset': {
                            borderColor: 'rgba(255, 215, 0, 0.5)',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#FFD700',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: '#FFD700',
                        },
                        '& .MuiOutlinedInput-input': {
                          color: '#FFFFFF',
                          backgroundColor: '#1a1a1a',
                        },
                      }}
                    />
                  </Collapse>

                  <Collapse in={showVolunteerCode}>
                    <TextField
                      fullWidth
                      label="Volunteer Code"
                      type="password"
                      variant="outlined"
                      value={volunteerCode}
                      onChange={e => setVolunteerCode(e.target.value)}
                      sx={{
                        mt: 2,
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#1a1a1a',
                          '& fieldset': {
                            borderColor: 'rgba(255, 215, 0, 0.3)',
                          },
                          '&:hover fieldset': {
                            borderColor: 'rgba(255, 215, 0, 0.5)',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#FFD700',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: '#FFD700',
                        },
                        '& .MuiOutlinedInput-input': {
                          color: '#FFFFFF',
                          backgroundColor: '#1a1a1a',
                        },
                      }}
                    />
                  </Collapse>
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  sx={{
                    bgcolor: '#FFD700',
                    color: '#000000',
                    py: 1.5,
                    fontWeight: 'bold',
                    '&:hover': {
                      bgcolor: '#e6c200',
                    },
                  }}
                >
                  {isLogin ? 'Login' : 'Sign Up'}
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={toggleAuthMode}
                  sx={{
                    borderColor: 'rgba(255, 215, 0, 0.5)',
                    color: '#FFD700',
                    py: 1.5,
                    '&:hover': {
                      borderColor: '#FFD700',
                      bgcolor: 'rgba(255, 215, 0, 0.1)',
                    },
                  }}
                >
                  {isLogin ? 'Create Account' : 'Back to Login'}
                </Button>
              </Box>
            </form>
          </Paper>
        </Grid>

        {/* Right side - Features */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: 3, md: 4 },
            }}
          >
            {/* Platform Features */}
            <Card
              sx={{
                flex: 1,
                bgcolor: 'transparent',
                background:
                  'linear-gradient(145deg, rgba(26, 26, 26, 0.9) 0%, rgba(42, 42, 42, 0.9) 100%)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 215, 0, 0.2)',
                borderRadius: 2,
                p: { xs: 3, md: 4 },
                position: 'relative',
                overflow: 'hidden',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  background:
                    'linear-gradient(45deg, transparent 65%, rgba(255, 215, 0, 0.1) 100%)',
                  pointerEvents: 'none',
                },
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  color: '#FFD700',
                  mb: 3,
                  fontWeight: 'bold',
                  textAlign: 'center',
                  textShadow: '0 0 20px rgba(255, 215, 0, 0.3)',
                }}
              >
                Platform Features
              </Typography>
              <Grid container spacing={3}>
                {features.map((feature, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 2,
                        background:
                          'linear-gradient(145deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)',
                        borderRadius: 1,
                        height: '100%',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        cursor: 'pointer',
                        position: 'relative',
                        overflow: 'hidden',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          background:
                            'linear-gradient(145deg, rgba(255, 215, 0, 0.1) 0%, rgba(255, 215, 0, 0.05) 100%)',
                          '& .feature-icon': {
                            transform: 'scale(1.1) rotate(5deg)',
                          },
                        },
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          background:
                            'linear-gradient(45deg, transparent 65%, rgba(255, 215, 0, 0.1) 100%)',
                          opacity: 0,
                          transition: 'opacity 0.3s ease',
                        },
                        '&:hover::after': {
                          opacity: 1,
                        },
                      }}
                    >
                      <Box
                        className="feature-icon"
                        sx={{
                          color: '#FFD700',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'transform 0.3s ease',
                          transform: 'scale(1)',
                        }}
                      >
                        {feature.icon}
                      </Box>
                      <Box>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            color: '#FFFFFF',
                            fontWeight: 'bold',
                            textShadow: '0 0 10px rgba(255, 215, 0, 0.2)',
                          }}
                        >
                          {feature.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#CCCCCC' }}>
                          {feature.description}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Card>

            {/* Role Features Grid */}
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Card
                  sx={{
                    height: '100%',
                    bgcolor: 'transparent',
                    background:
                      'linear-gradient(145deg, rgba(26, 26, 26, 0.9) 0%, rgba(42, 42, 42, 0.9) 100%)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 215, 0, 0.2)',
                    borderRadius: 2,
                    p: { xs: 3, md: 4 },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      border: '1px solid rgba(255, 215, 0, 0.4)',
                      '& .role-icon': {
                        transform: 'scale(1.1) rotate(5deg)',
                      },
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <AdminPanelSettingsIcon
                      className="role-icon"
                      sx={{
                        color: '#FFD700',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                    <Typography
                      variant="h6"
                      sx={{
                        color: '#FFD700',
                        textShadow: '0 0 10px rgba(255, 215, 0, 0.2)',
                      }}
                    >
                      Admin Features
                    </Typography>
                  </Box>
                  <List dense>
                    {roleFeatures.admin.map((feature, index) => (
                      <ListItem
                        key={index}
                        sx={{
                          transition: 'transform 0.2s ease',
                          '&:hover': {
                            transform: 'translateX(8px)',
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <Box
                            sx={{
                              width: 6,
                              height: 6,
                              bgcolor: '#FFD700',
                              borderRadius: '50%',
                              boxShadow: '0 0 10px rgba(255, 215, 0, 0.3)',
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={feature}
                          sx={{
                            '& .MuiListItemText-primary': {
                              color: '#FFFFFF',
                              transition: 'color 0.2s ease',
                            },
                            '&:hover .MuiListItemText-primary': {
                              color: '#FFD700',
                            },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Card
                  sx={{
                    height: '100%',
                    bgcolor: 'transparent',
                    background:
                      'linear-gradient(145deg, rgba(26, 26, 26, 0.9) 0%, rgba(42, 42, 42, 0.9) 100%)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 215, 0, 0.2)',
                    borderRadius: 2,
                    p: { xs: 3, md: 4 },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      border: '1px solid rgba(255, 215, 0, 0.4)',
                      '& .role-icon': {
                        transform: 'scale(1.1) rotate(5deg)',
                      },
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <VolunteerActivismIcon
                      className="role-icon"
                      sx={{
                        color: '#FFD700',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                    <Typography
                      variant="h6"
                      sx={{
                        color: '#FFD700',
                        textShadow: '0 0 10px rgba(255, 215, 0, 0.2)',
                      }}
                    >
                      Volunteer Features
                    </Typography>
                  </Box>
                  <List dense>
                    {roleFeatures.volunteer.map((feature, index) => (
                      <ListItem
                        key={index}
                        sx={{
                          transition: 'transform 0.2s ease',
                          '&:hover': {
                            transform: 'translateX(8px)',
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <Box
                            sx={{
                              width: 6,
                              height: 6,
                              bgcolor: '#FFD700',
                              borderRadius: '50%',
                              boxShadow: '0 0 10px rgba(255, 215, 0, 0.3)',
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={feature}
                          sx={{
                            '& .MuiListItemText-primary': {
                              color: '#FFFFFF',
                              transition: 'color 0.2s ease',
                            },
                            '&:hover .MuiListItemText-primary': {
                              color: '#FFD700',
                            },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Auth;
